/**
 * Desktop Bridge — typed interface between web app and Electron IPC
 *
 * Phase 16.1.1: Provides a unified API that works in both web and desktop modes.
 * In web mode, methods return sensible defaults or throw clear errors.
 * In desktop mode, methods delegate to window.electronAPI.
 *
 * Phase 16.2.3: Auto-update API (getUpdateStatus, checkForUpdate, installUpdate, onUpdateStatus).
 *
 * Pure logic module — no React dependencies. Fully testable.
 */

// ===== Detection =====

/** Returns true when running inside Electron desktop shell */
export function isDesktop(): boolean {
  return typeof window !== 'undefined' && window.electronAPI?.isDesktop === true;
}

/** Returns the OS platform ('win32' | 'darwin' | 'linux' | 'browser') */
export function getPlatform(): string {
  if (isDesktop()) return window.electronAPI!.platform;
  return 'browser';
}

// ===== Dialogs =====

export interface FileFilter {
  name: string;
  extensions: string[];
}

export interface SaveDialogOptions {
  defaultPath?: string;
  filters?: FileFilter[];
}

export interface OpenDialogOptions {
  title?: string;
  defaultPath?: string;
  filters?: FileFilter[];
  properties?: ('openFile' | 'openDirectory' | 'multiSelections')[];
}

/**
 * Show native save-file dialog.
 * @returns Chosen file path or null if cancelled.
 * @throws Error in web mode (no native dialogs available).
 */
export async function showSaveDialog(options: SaveDialogOptions = {}): Promise<string | null> {
  if (!isDesktop()) throw new Error('showSaveDialog is only available in desktop mode');
  return window.electronAPI!.showSaveDialog(options);
}

/**
 * Show native open-file/directory dialog.
 * @returns Array of chosen paths, empty if cancelled.
 * @throws Error in web mode.
 */
export async function showOpenDialog(options: OpenDialogOptions = {}): Promise<string[]> {
  if (!isDesktop()) throw new Error('showOpenDialog is only available in desktop mode');
  return window.electronAPI!.showOpenDialog(options);
}

// ===== File system =====

/**
 * Read a file from the local filesystem.
 * @returns File content as UTF-8 string.
 * @throws Error in web mode or if file doesn't exist.
 */
export async function readLocalFile(filePath: string): Promise<string> {
  if (!isDesktop()) throw new Error('readLocalFile is only available in desktop mode');
  return window.electronAPI!.readFile(filePath);
}

/**
 * Write a string to a local file (UTF-8).
 * @throws Error in web mode.
 */
export async function writeLocalFile(filePath: string, content: string): Promise<void> {
  if (!isDesktop()) throw new Error('writeLocalFile is only available in desktop mode');
  return window.electronAPI!.writeFile(filePath, content);
}

/**
 * Check if a file or directory exists.
 * @returns false in web mode (nothing exists on "disk").
 */
export async function fileExists(filePath: string): Promise<boolean> {
  if (!isDesktop()) return false;
  return window.electronAPI!.exists(filePath);
}

/**
 * Create a directory recursively.
 * @throws Error in web mode.
 */
export async function mkdirLocal(dirPath: string): Promise<void> {
  if (!isDesktop()) throw new Error('mkdirLocal is only available in desktop mode');
  return window.electronAPI!.mkdir(dirPath);
}

// ===== App settings =====

/**
 * Retrieve a setting value from persistent desktop storage.
 * Falls back to localStorage in web mode for parity.
 */
export async function getSetting<T = unknown>(key: string): Promise<T | null> {
  if (isDesktop()) {
    const value = await window.electronAPI!.getSetting(key);
    return (value ?? null) as T | null;
  }
  // Web fallback: localStorage
  const raw = localStorage.getItem(`nf-settings:${key}`);
  if (raw === null) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

/**
 * Retrieve all settings as a key-value object.
 * Falls back to an empty object in web mode (no full-scan equivalent).
 */
export async function getAllSettings(): Promise<Record<string, unknown>> {
  if (isDesktop()) {
    return (await window.electronAPI!.getSetting()) as Record<string, unknown>;
  }
  return {};
}

/**
 * Persist a setting value.
 * Uses desktop JSON file in Electron, localStorage in web.
 */
export async function setSetting(key: string, value: unknown): Promise<void> {
  if (isDesktop()) {
    return window.electronAPI!.setSetting(key, value);
  }
  localStorage.setItem(`nf-settings:${key}`, JSON.stringify(value));
}

/**
 * Delete a setting.
 */
export async function deleteSetting(key: string): Promise<void> {
  if (isDesktop()) {
    return window.electronAPI!.deleteSetting(key);
  }
  localStorage.removeItem(`nf-settings:${key}`);
}

// ===== App info =====

/**
 * Get the app version string.
 * Returns 'web' in browser mode.
 */
export async function getAppVersion(): Promise<string> {
  if (isDesktop()) return window.electronAPI!.getVersion();
  return 'web';
}

/** Standard OS path names that can be resolved */
export type PathName = 'userData' | 'documents' | 'downloads' | 'temp' | 'home';

/**
 * Get a standard OS path (documents, downloads, etc.).
 * @throws Error in web mode.
 */
export async function getAppPath(name: PathName): Promise<string> {
  if (!isDesktop()) throw new Error('getAppPath is only available in desktop mode');
  return window.electronAPI!.getPath(name);
}

// ===== Convenience: Save file with dialog =====

/**
 * High-level: show save dialog + write file in one call.
 * @returns The saved file path, or null if user cancelled.
 */
export async function saveFileWithDialog(
  content: string,
  options: SaveDialogOptions = {},
): Promise<string | null> {
  const filePath = await showSaveDialog(options);
  if (!filePath) return null;
  await writeLocalFile(filePath, content);
  return filePath;
}

/**
 * High-level: show open dialog + read file in one call.
 * @returns Array of { path, content } for each selected file.
 */
export async function openFilesWithDialog(
  options: OpenDialogOptions = {},
): Promise<Array<{ path: string; content: string }>> {
  const paths = await showOpenDialog(options);
  const results: Array<{ path: string; content: string }> = [];
  for (const p of paths) {
    const content = await readLocalFile(p);
    results.push({ path: p, content });
  }
  return results;
}

// ===== Auto-update (Phase 16.2.3) =====

/** Update status shape — mirrors Electron main process UpdateStatus */
export interface UpdateStatus {
  state: 'idle' | 'checking' | 'available' | 'not-available' | 'downloading' | 'downloaded' | 'error';
  version?: string;
  releaseNotes?: string;
  percent?: number;
  bytesPerSecond?: number;
  error?: string;
}

// ===== Security helpers (CR Round 81) =====

/** Allowed schemes for external URL opening — matches main process validation */
const SAFE_EXTERNAL_SCHEMES = new Set(['http:', 'https:', 'mailto:']);

/**
 * Validate that a URL is safe to open externally.
 * Prevents file://, javascript:, custom-protocol:// attacks.
 * Used by both main process and renderer-side guards.
 */
export function isSafeExternalURL(urlString: string): boolean {
  try {
    const parsed = new URL(urlString);
    return SAFE_EXTERNAL_SCHEMES.has(parsed.protocol);
  } catch {
    return false;
  }
}

/** Idle status returned in web mode (no updates available outside desktop) */
const WEB_IDLE_STATUS: UpdateStatus = { state: 'idle' };

/**
 * Get the current auto-update status.
 * Returns idle in web mode.
 */
export async function getUpdateStatus(): Promise<UpdateStatus> {
  if (!isDesktop()) return WEB_IDLE_STATUS;
  return window.electronAPI!.getUpdateStatus() as Promise<UpdateStatus>;
}

/**
 * Trigger a manual update check.
 * No-op in web mode.
 */
export async function checkForUpdate(): Promise<void> {
  if (!isDesktop()) return;
  return window.electronAPI!.checkForUpdate();
}

/**
 * Install a downloaded update (quits app, installs, restarts).
 * @throws Error in web mode.
 */
export async function installUpdate(): Promise<void> {
  if (!isDesktop()) throw new Error('installUpdate is only available in desktop mode');
  return window.electronAPI!.installUpdate();
}

/**
 * Subscribe to real-time update status events from the main process.
 * In web mode, returns a no-op unsubscribe function.
 * @returns Unsubscribe function.
 */
export function onUpdateStatus(callback: (status: UpdateStatus) => void): () => void {
  if (!isDesktop()) return () => {};
  return window.electronAPI!.onUpdateStatus(callback);
}

/**
 * Check if an update is ready to install.
 * Pure helper for UI components.
 */
export function isUpdateReady(status: UpdateStatus): boolean {
  return status.state === 'downloaded';
}

/**
 * Check if an update check or download is in progress.
 * Pure helper for UI loading states.
 */
export function isUpdateBusy(status: UpdateStatus): boolean {
  return status.state === 'checking' || status.state === 'downloading';
}

/**
 * Format download progress as a human-readable string.
 * Returns empty string for non-downloading states.
 */
export function formatUpdateProgress(status: UpdateStatus): string {
  if (status.state !== 'downloading') return '';
  const pct = status.percent ?? 0;
  if (status.bytesPerSecond && status.bytesPerSecond > 0) {
    const speed = status.bytesPerSecond >= 1_048_576
      ? `${(status.bytesPerSecond / 1_048_576).toFixed(1)} MB/s`
      : `${Math.round(status.bytesPerSecond / 1_024)} KB/s`;
    return `${pct}% — ${speed}`;
  }
  return `${pct}%`;
}

/**
 * Get a user-facing message for the current update state.
 */
export function getUpdateMessage(status: UpdateStatus): string {
  switch (status.state) {
    case 'idle': return '';
    case 'checking': return '正在检查更新…';
    case 'available': return status.version ? `发现新版本 ${status.version}` : '发现新版本';
    case 'not-available': return '已是最新版本';
    case 'downloading': {
      const progress = formatUpdateProgress(status);
      return progress ? `正在下载更新 ${progress}` : '正在下载更新';
    }
    case 'downloaded': return status.version
      ? `新版本 ${status.version} 已就绪，重启即可安装`
      : '新版本已就绪，重启即可安装';
    case 'error': return `更新失败: ${status.error ?? '未知错误'}`;
  }
}